import apiClient from './apiClient';

export type CEFRLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';

export interface NormalizedScores {
    complexity: number;
    accuracy: number;
    fluency: number;
    functional_adequacy: number;
    overall: number;
}

export interface FADimension {
    score: number;
    evidence: string;
    justification: string;
}

export interface FunctionalAdequacyResult {
    content: FADimension;
    task_fulfillment: FADimension;
    coherence: FADimension;
    appropriateness: FADimension;
    overall_comment: string;
}

export type ErrorCategory = 'morphological' | 'syntactic' | 'lexical' | 'orthographic';

export interface GrammarError {
    char_start: number;
    char_end: number;
    original: string;
    correction: string;
    category: ErrorCategory;
    subtype: string;
    explanation: string;
}

export interface TUnit {
    char_start: number;
    char_end: number;
    text: string;
    clause_count: number;
    dependent_clause_count: number;
    complex_nominal_count: number;
    coordinate_phrase_count: number;
    has_error: boolean;
    error_count: number;
}

export interface EvaluateWritingRequest {
    text: string;
    targetCEFR: CEFRLevel;
    taskPrompt?: string;
    elapsedSeconds?: number;
    submissionId?: string;
    gateType?: string;
}

export interface EvaluateWritingResponse {
    historyId: string;
    cefrEstimate: CEFRLevel;
    normalized: NormalizedScores;
    raw: Record<string, unknown>;
    parse: {
        sentence_count: number;
        t_units: TUnit[];
        errors: GrammarError[];
        parse_confidence: number;
    };
    fa: FunctionalAdequacyResult;
    validation: { passed: boolean; warnings: string[] };
}

export interface AbilityVectorResponse {
    exists: boolean;
    complexity: number | null;
    accuracy: number | null;
    fluency: number | null;
    functional_adequacy: number | null;
    cefrEstimate: CEFRLevel | null;
    sampleCount: number;
    updatedAt?: string;
}

export interface CaffaHistoryPoint {
    id: string;
    createdAt: string;
    modality: string;
    targetCefr: CEFRLevel;
    cefrEstimate: CEFRLevel | null;
    complexity: number;
    accuracy: number;
    fluency: number;
    functional_adequacy: number;
    overall: number;
    errorCount: number;
}

export async function evaluateWriting(
    req: EvaluateWritingRequest,
): Promise<EvaluateWritingResponse> {
    const res = await apiClient.post<EvaluateWritingResponse>(
        '/caffa/evaluate/writing',
        req,
    );
    return res.data;
}

export async function getMyCaffaAbilityVector(): Promise<AbilityVectorResponse> {
    const res = await apiClient.get<AbilityVectorResponse>(
        '/caffa/ability-vector/me',
    );
    return res.data;
}

export async function getMyCaffaHistory(limit = 30): Promise<CaffaHistoryPoint[]> {
    const res = await apiClient.get<{ items: CaffaHistoryPoint[] }>(
        '/caffa/history/me',
        { params: { limit } },
    );
    return res.data.items;
}
